import type {
  SmartNotification,
  NotificationDelivery,
  EngagementPattern,
  TimingPreference,
  AIError,
} from '@coffeeplexity/shared';

export interface NotificationEngagementData {
  notificationId: string;
  userId: string;
  engagementType: 'delivered' | 'opened' | 'clicked' | 'dismissed' | 'expired';
  timestamp: string;
  context: {
    source: string;
    deviceType: string;
    timeOfDay: number;
    dayOfWeek: number;
    appState: 'active' | 'background' | 'inactive';
    batteryLevel?: number;
    networkType?: string;
    location?: {
      latitude: number;
      longitude: number;
    };
  };
  metadata?: Record<string, any>;
}

export interface NotificationFeedback {
  notificationId: string;
  userId: string;
  feedbackType:
    | 'helpful'
    | 'not_helpful'
    | 'too_frequent'
    | 'wrong_time'
    | 'irrelevant';
  rating?: number; // 1-5 scale
  comment?: string;
  timestamp: string;
  context: Record<string, any>;
}

export interface UserPatternAnalysis {
  userId: string;
  optimalTimes: string[];
  engagementPatterns: EngagementPattern[];
  timingPreferences: TimingPreference[];
  frequencyPreference: number;
  channelPreferences: {
    push: number;
    email: number;
    in_app: number;
  };
  contentPreferences: Record<string, number>;
  lastAnalyzed: string;
}

class NotificationEngagementService {
  private baseUrl: string;
  private apiKey: string;

  constructor(baseUrl: string = '/api/ai/notifications', apiKey: string = '') {
    this.baseUrl = baseUrl;
    this.apiKey = apiKey;
  }

  /**
   * Record notification engagement event
   */
  async recordEngagement(
    engagementData: NotificationEngagementData
  ): Promise<NotificationDelivery> {
    try {
      const response = await fetch(`${this.baseUrl}/engagement`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(this.apiKey && { Authorization: `Bearer ${this.apiKey}` }),
        },
        body: JSON.stringify({
          ...engagementData,
          timestamp: engagementData.timestamp || new Date().toISOString(),
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to record engagement: ${response.statusText}`);
      }

      return await response.json();
    } catch (error: any) {
      const aiError: AIError = {
        code: 'ENGAGEMENT_RECORD_ERROR',
        message: error.message || 'Failed to record notification engagement',
        recoverable: true,
        fallbackStrategy: 'rule_based',
      };
      throw aiError;
    }
  }

  /**
   * Submit user feedback on notification
   */
  async submitFeedback(feedback: NotificationFeedback): Promise<void> {
    try {
      const response = await fetch(`${this.baseUrl}/feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(this.apiKey && { Authorization: `Bearer ${this.apiKey}` }),
        },
        body: JSON.stringify({
          ...feedback,
          timestamp: feedback.timestamp || new Date().toISOString(),
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to submit feedback: ${response.statusText}`);
      }
    } catch (error: any) {
      const aiError: AIError = {
        code: 'FEEDBACK_SUBMIT_ERROR',
        message: error.message || 'Failed to submit notification feedback',
        recoverable: true,
        fallbackStrategy: 'rule_based',
      };
      throw aiError;
    }
  }

  /**
   * Get user engagement patterns analysis
   */
  async getUserPatternAnalysis(userId: string): Promise<UserPatternAnalysis> {
    try {
      const response = await fetch(`${this.baseUrl}/patterns/${userId}`, {
        headers: {
          ...(this.apiKey && { Authorization: `Bearer ${this.apiKey}` }),
        },
      });

      if (!response.ok) {
        throw new Error(
          `Failed to get pattern analysis: ${response.statusText}`
        );
      }

      return await response.json();
    } catch (error: any) {
      const aiError: AIError = {
        code: 'PATTERN_ANALYSIS_ERROR',
        message: error.message || 'Failed to get user pattern analysis',
        recoverable: true,
        fallbackStrategy: 'rule_based',
      };
      throw aiError;
    }
  }

  /**
   * Trigger pattern learning update
   */
  async triggerPatternLearning(userId: string): Promise<void> {
    try {
      const response = await fetch(`${this.baseUrl}/learn/${userId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(this.apiKey && { Authorization: `Bearer ${this.apiKey}` }),
        },
      });

      if (!response.ok) {
        throw new Error(
          `Failed to trigger pattern learning: ${response.statusText}`
        );
      }
    } catch (error: any) {
      const aiError: AIError = {
        code: 'PATTERN_LEARNING_ERROR',
        message: error.message || 'Failed to trigger pattern learning',
        recoverable: true,
        fallbackStrategy: 'rule_based',
      };
      throw aiError;
    }
  }

  /**
   * Get notification effectiveness metrics
   */
  async getEffectivenessMetrics(
    userId: string,
    timeRange: 'day' | 'week' | 'month' = 'week'
  ): Promise<{
    totalNotifications: number;
    engagementRate: number;
    clickThroughRate: number;
    dismissalRate: number;
    optimalTimeAccuracy: number;
    userSatisfactionScore: number;
    improvementSuggestions: string[];
  }> {
    try {
      const response = await fetch(
        `${this.baseUrl}/metrics/${userId}?timeRange=${timeRange}`,
        {
          headers: {
            ...(this.apiKey && { Authorization: `Bearer ${this.apiKey}` }),
          },
        }
      );

      if (!response.ok) {
        throw new Error(
          `Failed to get effectiveness metrics: ${response.statusText}`
        );
      }

      return await response.json();
    } catch (error: any) {
      const aiError: AIError = {
        code: 'METRICS_ERROR',
        message: error.message || 'Failed to get effectiveness metrics',
        recoverable: true,
        fallbackStrategy: 'rule_based',
      };
      throw aiError;
    }
  }

  /**
   * Record notification timing optimization result
   */
  async recordOptimizationResult(
    userId: string,
    optimizationId: string,
    result: {
      beforeEngagementRate: number;
      afterEngagementRate: number;
      improvement: number;
      confidence: number;
      factors: string[];
    }
  ): Promise<void> {
    try {
      const response = await fetch(`${this.baseUrl}/optimization-result`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(this.apiKey && { Authorization: `Bearer ${this.apiKey}` }),
        },
        body: JSON.stringify({
          userId,
          optimizationId,
          result,
          timestamp: new Date().toISOString(),
        }),
      });

      if (!response.ok) {
        throw new Error(
          `Failed to record optimization result: ${response.statusText}`
        );
      }
    } catch (error: any) {
      const aiError: AIError = {
        code: 'OPTIMIZATION_RESULT_ERROR',
        message: error.message || 'Failed to record optimization result',
        recoverable: true,
        fallbackStrategy: 'rule_based',
      };
      throw aiError;
    }
  }

  /**
   * Get contextual factors for current time
   */
  getContextualFactors(): NotificationEngagementData['context'] {
    const now = new Date();

    return {
      source: 'notification_center',
      deviceType: 'mobile', // Could be detected from user agent
      timeOfDay: now.getHours(),
      dayOfWeek: now.getDay(),
      appState: 'active', // Would be detected from app state
      // Additional context would be gathered from device APIs
    };
  }

  /**
   * Analyze notification timing effectiveness
   */
  analyzeTimingEffectiveness(deliveries: NotificationDelivery[]): {
    bestHours: number[];
    worstHours: number[];
    bestDays: number[];
    worstDays: number[];
    overallEngagementRate: number;
  } {
    if (deliveries.length === 0) {
      return {
        bestHours: [],
        worstHours: [],
        bestDays: [],
        worstDays: [],
        overallEngagementRate: 0,
      };
    }

    // Group by hour and day
    const hourlyStats: Record<number, { total: number; engaged: number }> = {};
    const dailyStats: Record<number, { total: number; engaged: number }> = {};
    let totalEngaged = 0;

    deliveries.forEach(delivery => {
      const deliveryTime = new Date(delivery.deliveredAt);
      const hour = deliveryTime.getHours();
      const day = deliveryTime.getDay();
      const isEngaged = !!(delivery.openedAt || delivery.clickedAt);

      // Hour stats
      if (!hourlyStats[hour]) {
        hourlyStats[hour] = { total: 0, engaged: 0 };
      }
      hourlyStats[hour].total++;
      if (isEngaged) {
        hourlyStats[hour].engaged++;
        totalEngaged++;
      }

      // Day stats
      if (!dailyStats[day]) {
        dailyStats[day] = { total: 0, engaged: 0 };
      }
      dailyStats[day].total++;
      if (isEngaged) {
        dailyStats[day].engaged++;
      }
    });

    // Calculate engagement rates and sort
    const hourlyRates = Object.entries(hourlyStats)
      .map(([hour, stats]) => ({
        hour: parseInt(hour),
        rate: stats.engaged / stats.total,
        total: stats.total,
      }))
      .filter(item => item.total >= 3); // Minimum sample size

    const dailyRates = Object.entries(dailyStats)
      .map(([day, stats]) => ({
        day: parseInt(day),
        rate: stats.engaged / stats.total,
        total: stats.total,
      }))
      .filter(item => item.total >= 3);

    hourlyRates.sort((a, b) => b.rate - a.rate);
    dailyRates.sort((a, b) => b.rate - a.rate);

    return {
      bestHours: hourlyRates.slice(0, 3).map(item => item.hour),
      worstHours: hourlyRates.slice(-3).map(item => item.hour),
      bestDays: dailyRates.slice(0, 3).map(item => item.day),
      worstDays: dailyRates.slice(-3).map(item => item.day),
      overallEngagementRate: totalEngaged / deliveries.length,
    };
  }

  /**
   * Generate personalized notification timing recommendations
   */
  generateTimingRecommendations(userPatterns: UserPatternAnalysis): {
    recommendations: string[];
    confidence: number;
    reasoning: string[];
  } {
    const recommendations: string[] = [];
    const reasoning: string[] = [];
    let confidence = 0.5;

    // Analyze optimal times
    if (userPatterns.optimalTimes.length > 0) {
      recommendations.push(
        `Best times to send notifications: ${userPatterns.optimalTimes.join(', ')}`
      );
      reasoning.push('Based on your historical engagement patterns');
      confidence += 0.2;
    }

    // Analyze frequency preference
    if (userPatterns.frequencyPreference > 0) {
      if (userPatterns.frequencyPreference <= 3) {
        recommendations.push('Reduce notification frequency to avoid fatigue');
        reasoning.push('You tend to engage less with frequent notifications');
      } else if (userPatterns.frequencyPreference >= 8) {
        recommendations.push('You can handle more frequent notifications');
        reasoning.push(
          'You consistently engage with multiple daily notifications'
        );
      }
      confidence += 0.1;
    }

    // Analyze channel preferences
    const topChannel = Object.entries(userPatterns.channelPreferences).sort(
      ([, a], [, b]) => b - a
    )[0];

    if (topChannel && topChannel[1] > 0.6) {
      recommendations.push(`Focus on ${topChannel[0]} notifications`);
      reasoning.push(`You engage most with ${topChannel[0]} notifications`);
      confidence += 0.1;
    }

    // Analyze timing patterns
    const morningEngagement = userPatterns.timingPreferences.filter(
      pref => pref.hourOfDay >= 6 && pref.hourOfDay <= 11
    );
    const eveningEngagement = userPatterns.timingPreferences.filter(
      pref => pref.hourOfDay >= 18 && pref.hourOfDay <= 22
    );

    if (morningEngagement.length > eveningEngagement.length) {
      recommendations.push('Schedule more notifications for morning hours');
      reasoning.push('You show higher engagement in the morning');
      confidence += 0.1;
    } else if (eveningEngagement.length > morningEngagement.length) {
      recommendations.push('Schedule more notifications for evening hours');
      reasoning.push('You show higher engagement in the evening');
      confidence += 0.1;
    }

    return {
      recommendations,
      confidence: Math.min(confidence, 1.0),
      reasoning,
    };
  }
}

export const notificationEngagementService =
  new NotificationEngagementService();
